import { MasterRepository } from 'application/repositories/MasterRepository';
import {
    MasterAreaData,
    MasterAreaResponse,
    MasterCompanies,
    MasterLines,
    MasterStations,
    MasterStationsWithSearchKeyResponse,
    MasterSubCategory,
    MasterSubCategoryData,
    MasterWebAppUrl,
    SystemSetting,
} from 'domain/entities/MasterEntity';
import {
    DetailConfirmPropertyResponse,
    DetailPropertyConfirmPayload,
    DetailPropertyPayload,
    DetailPropertyResponse,
} from 'presentation/pages/CU/CU-062-01/models/CU-062-01.model';
import { DEFAULT_PARAMS } from 'utilities/constants/PARAMETER';

export class MasterUseCase implements MasterUseCaseInterface {
    private masterRepo: MasterRepository;
    constructor(masterRepo: MasterRepository) {
        this.masterRepo = masterRepo;
    }

    async getMasterAreasSearchAreas(): Promise<MasterAreaData[]> {
        const firstResponse = await this.masterRepo.getMasterAreasSearchAreas({
            page: 1,
            pageSize: DEFAULT_PARAMS.SIZE_ALL,
        });
        return firstResponse.result?.data;
    }
    async getMasterAreasSearchAreasForSearch(): Promise<MasterAreaData[]> {
        const firstResponse = await this.masterRepo.getMasterAreasSearchAreasForSearch({
            page: 1,
            pageSize: DEFAULT_PARAMS.SIZE_ALL,
        });
        return firstResponse.result?.data;
    }
    async getMasterAreasFitList(params: {
        areaId: number;
        level: number;
    }): Promise<MasterAreaResponse> {
        return await this.masterRepo.getMasterAreasFitList({
            ...params,
            page: 1,
            pageSize: DEFAULT_PARAMS.SIZE_ALL,
        });
    }

    async getMasterSubCategoryList(params: {
        categoryCode: string;
    }): Promise<MasterSubCategoryData> {
        return await this.masterRepo.getMasterSubCategoryList(params);
    }

    async getMasterSubCategoryGroupList(params: { categoryCodes: string[] }): Promise<any> {
        const response = await this.masterRepo.getMasterSubCategoryGroupList(params);
        if (response?.result) {
            const listData = response.result;
            const listDataGroup: any = {};
            listData.forEach((item: any) => {
                if (!listDataGroup[item.categoryCode]) {
                    listDataGroup[item.categoryCode] = [];
                }
                listDataGroup[item.categoryCode].push(item);
            });
            return listDataGroup;
        }
        return [];
    }

    async getMasterSubCategoryGroupListToOptions(params: {
        categoryCodes: string[];
    }): Promise<any> {
        const response = await this.masterRepo.getMasterSubCategoryGroupList(params);
        if (response?.result) {
            const listData = response.result;
            const listDataGroup: any = {};
            listData.forEach((item: MasterSubCategory) => {
                if (!listDataGroup[item.categoryCode]) {
                    listDataGroup[item.categoryCode] = [];
                }
                listDataGroup[item.categoryCode].push({
                    label: item.codeName,
                    value: item.code,
                });
            });
            return listDataGroup;
        }
        return [];
    }

    async putMasterPropertyRegisterListingInformation(data: {
        mstPropertyId: number;
    }): Promise<MasterSubCategoryData> {
        return await this.masterRepo.putMasterPropertyRegisterListingInformation(data);
    }

    async getMasterCompaniesList(params: {
        areaId: number;
        keyword?: string;
        sortBy: string;
    }): Promise<MasterCompanies[]> {
        const response = await this.masterRepo.getMasterCompaniesList({
            ...params,
            page: 1,
            pageSize: DEFAULT_PARAMS.SIZE_ALL,
        });
        return response?.result?.data ?? [];
    }

    async getMasterLinesList(params: {
        id: number;
        areaId: number;
        keyword?: string;
        sortBy: string;
    }): Promise<MasterLines[]> {
        const response = await this.masterRepo.getMasterLinesList({
            ...params,
            page: 1,
            pageSize: DEFAULT_PARAMS.SIZE_ALL,
        });
        return response?.result?.data ?? [];
    }

    async getMasterStationsList(params: {
        id: number;
        areaId: number;
        keyword?: string;
    }): Promise<MasterStations[]> {
        const response = await this.masterRepo.getMasterStationsList({
            ...params,
            page: 1,
            pageSize: DEFAULT_PARAMS.SIZE_ALL,
        });
        return response?.result?.data ?? [];
    }
    async getSystemSettingList(params: { keys: string[] }): Promise<SystemSetting[]> {
        const response = await this.masterRepo.getSystemSettingList(params);
        return response?.result ?? [];
    }
    async getMasterWepAppUrl(type: number): Promise<MasterWebAppUrl> {
        const response = await this.masterRepo.getMasterWepAppUrl(type);
        return response;
    }

    async getSettingProperty(params: {
        settlementId: number;
        scheduleAdjustmentDiv: number;
    }): Promise<DetailPropertyResponse> {
        const response = await this.masterRepo.getSettingProperty(params);
        return response;
    }
    async getListOptionTime(): Promise<any> {
        const response = await this.masterRepo.getListOptionTime();
        return response;
    }
    async updateSettingProperty(payload: DetailPropertyPayload): Promise<any> {
        const response = await this.masterRepo.updateSettingProperty(payload);
        return response;
    }
    async getConfirmSettingProperty(
        params: DetailPropertyConfirmPayload,
    ): Promise<DetailConfirmPropertyResponse> {
        const response = await this.masterRepo.getConfirmSettingProperty(params);
        return response;
    }
    async confirmSettingProperty(payload: DetailPropertyConfirmPayload): Promise<any> {
        const response = await this.masterRepo.confirmSettingProperty(payload);
        return response;
    }
    async getMasterStationByKeys(key: any): Promise<MasterStationsWithSearchKeyResponse> {
        const response = await this.masterRepo.getMasterStationByKeys(key);
        return response;
    }
}

export interface MasterUseCaseInterface {
    getMasterAreasSearchAreas(): Promise<MasterAreaData[]>;
    getMasterAreasSearchAreasForSearch(): Promise<MasterAreaData[]>;
    getMasterAreasFitList(params: { areaId: number; level: number }): Promise<MasterAreaResponse>;
    getMasterSubCategoryList(params: { categoryCode: string }): Promise<MasterSubCategoryData>;
    getMasterSubCategoryGroupList(params: { categoryCodes: string[] }): Promise<any>;
    getMasterSubCategoryGroupListToOptions(params: { categoryCodes: string[] }): Promise<any>;
    putMasterPropertyRegisterListingInformation(data: {
        mstPropertyId: number;
    }): Promise<MasterSubCategoryData>;
    getMasterCompaniesList(params: {
        areaId: number;
        keyword?: string;
        sortBy: string;
    }): Promise<MasterCompanies[]>;
    getMasterLinesList(params: {
        id: number;
        areaId: number;
        keyword?: string;
        sortBy: string;
    }): Promise<MasterLines[]>;
    getMasterStationsList(params: {
        id: number;
        areaId: number;
        keyword?: string;
    }): Promise<MasterStations[]>;
    getSystemSettingList(params: { keys: string[] }): Promise<SystemSetting[]>;
    getMasterWepAppUrl(type: number): Promise<MasterWebAppUrl>;
    getSettingProperty(params: {
        settlementId: number;
        scheduleAdjustmentDiv: number;
    }): Promise<DetailPropertyResponse>;
    getListOptionTime(): Promise<any>;
    updateSettingProperty(payload: DetailPropertyPayload): Promise<any>;
    getConfirmSettingProperty(
        params: DetailPropertyConfirmPayload,
    ): Promise<DetailConfirmPropertyResponse>;
    confirmSettingProperty(payload: DetailPropertyConfirmPayload): Promise<any>;
    getMasterStationByKeys(key: any): Promise<MasterStationsWithSearchKeyResponse>;
}
